//./react-app/src/App.tsx
import React, { FC, useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from './helpers/apiUtils';
import AdServerStatistics from './pages/AdServerStatistics';
import { ColorModeContext, useMode } from './theme';
import { CssBaseline, ThemeProvider } from '@mui/material';
import Sidebar from './scenes/global/Sidebar';
import Dashboard from './scenes/dashboard';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import SeitenwechselScroll from './components/SeitenwechselScroll';
import { resolveRouteRedirect } from './helpers/routeAccess';
import MsmPartnerPage from './scenes/msm-partner';
import { Helmet } from 'react-helmet';
import { FilterContext, IFilterState } from './contexts/FilterContext';
import { UserContext, UserInfo } from './contexts/UserContext';
import FilterBar from './components/FilterBar';
import Calendar from './scenes/calendar';
import { Login } from './components/LoginComponent';
import moment from 'moment';
import LeadTurnoverPage from './scenes/gurupress/lead-turnover';
import Newsletter from './scenes/newsletter';
import ArticleStatistics from './scenes/articles';
import TrafficLeads from './scenes/traffic-leads';
import SoldLeads from './scenes/sold-leads';
import ProductStatisticsPage from './scenes/adserver/productStatisticsPage';
import ListGrowth from './scenes/mailchimp';
import ClientStats from './scenes/mailchimp/ClientStats';
import AdFormStatisticsPage from './scenes/adserver/AdFormStatisticsPage';
import LeadImportsPage from './scenes/gurupress/lead-imports';
import PushStatisticsPage from './scenes/push/PushStatisticsPage';
import WebPushDashboard from './scenes/web-push';
import DnvMigrationDashboard from './scenes/dnv-migration';
import WelcomeCampaignPage from './scenes/welcome-campaign';
import ArticleEvaluationPage from './scenes/articles/ArticleEvaluationPage';
import WelcomeFlowsPage from './scenes/adserver/WelcomeFlowsPage';
import SamSchedulingPage from './scenes/sam-scheduling';
import AdserverSendPausesPage from './scenes/adserver-send-pauses';
import ExternalSamConfigPage from './scenes/external-sam-config';
import RoiEvaluationPage from './scenes/vnr/roi-evaluation';
import RoiTestsPage from './scenes/vnr/roi-tests';
import SnapshotEvaluationPage from './scenes/vnr/snapshot-evaluation';
import ExportedLeadsPage from './scenes/leads/exported-leads';
import UnifiedDailyControllingPage from './scenes/daily-controlling/unified';
import DailyControllingEmail from './scenes/TestPage';
import ArticleDistributionPage from './scenes/gurupress/article-distribution';
import DoiEvaluationPage from './scenes/doi-evaluation';
import DoiEvaluationOptinEmailsPage from './scenes/doi-evaluation-optin-emails';
import DoiEvaluationAffiliatesPage from './scenes/doi-evaluation-affiliates';
import DoiEvaluationAdrefPage from './scenes/doi-evaluation-adref';
import DoiProcessDetailsPage from './scenes/doi-process-details';
import CoregEfficiencyPage from './scenes/coreg-efficiency';
import LeadwallEvaluationPage from './scenes/leadwall';
import LeadwallChannelTestPage from './scenes/leadwall-channel-test';
import PlxOrdersPage from './scenes/plx-orders';
import PlxOrdersConfigPage from './scenes/plx-orders/config';
import PlxProductsPage from './scenes/plx-orders/products';
import PlxGroupingsPage from './scenes/plx-orders/groupings';
// P5 (27.10.2026): revenue-variance, anfo-valuation, cashflow-forecast, kuendigungsquoten sind in die Sektion
// eingebettet (/auswertungen/umsatz/*); ihre alten Pfade leiten über `Umleitung` samt Query dorthin.
import PlxPxImportPage from './scenes/plx-orders/px-import';
import SamTimeline from './scenes/sam-timeline';
import NewsletterAdsTimeline from './scenes/newsletter-ads-timeline';
import NewsletterAdInsertions from './scenes/newsletter-ad-insertions';
import Ga4TrafficPage from './scenes/ga4-traffic';
import BrokerHub from './scenes/broker-hub';
import BrokerContentHub from './scenes/broker-content-hub';
import AdStatistics from './scenes/ad-statistics';
import AnfoStatistics from './scenes/anfo-statistics';
import FtLifetimeValue from './scenes/ft-lifetime-value';
import AuswertungenUebersicht from './scenes/auswertungen';
import AuswertungenWebinareProdukte from './scenes/auswertungen/webinare-produkte';
import AuswertungenUmsatz from './scenes/auswertungen/umsatz';
import AuswertungenAnmeldungBestellung from './scenes/auswertungen/umsatz/AnmeldungBestellung';
import { CashflowUnterseite, KuendigungUnterseite } from './scenes/auswertungen/umsatz/Eingebettet';
import AuswertungenAnfoWertModell from './scenes/auswertungen/umsatz/AnfoWertModell';
import AuswertungenWebinarDetail from './scenes/auswertungen/webinar-detail';
import AuswertungenKanaele from './scenes/auswertungen/kanaele';
import AuswertungenHebel from './scenes/auswertungen/hebel';
import WebinarStatistics from './scenes/webinar-statistics';
import WebinarUserVerhalten from './scenes/webinar-user-verhalten';
// P5: /webinar-auswertungen/:id leitet auf die Sektions-Detailseite (dieselbe Komponente), siehe `Umleitung`.
// --- P5 --- Bestellungen, Werkzeuge, eingebettete Unterseiten, Umleitungen
import AuswertungenBestellungen from './scenes/auswertungen/bestellungen';
import AuswertungenWerkzeuge from './scenes/auswertungen/werkzeuge';
import {
  AnfoBewertungUnterseite,
  DiskrepanzenUnterseite,
} from './scenes/auswertungen/umsatz/Eingebettet';
import { AltSeiteHinweis, Umleitung } from './scenes/auswertungen/Umleitung';
import IterableStatistics from './scenes/iterable-statistics';
import VnrRevenue from './scenes/vnr-revenue';
import AllowedEmails from './scenes/admin/AllowedEmails';
import ErrorLog from './scenes/admin/ErrorLog';
import WebinarMailings from './scenes/webinar-mailings';
import WebinarManagement from './scenes/webinar-management';
import WebinarLp from './scenes/webinar-lp';
import WebinarVerteiler from './scenes/webinar-verteiler';
import PlxMailings from './scenes/plx-mailings';
import NewsletterRedlinks from './scenes/newsletter-redlinks';
import LeadSourceRulesPage from './scenes/settings/lead-source-rules';
import EtkpDevelopment from './scenes/etkp-development';
import EmailHistory from './scenes/email-history';
import PostmasterDashboard from './scenes/postmaster';
import AdEvaluation from './scenes/ad-evaluation';
import NfaMailings from './scenes/nfa-mailings';
import DnvAssetAnalysis from './scenes/dnv-asset-analysis';
import LiveAdmin from './scenes/live-admin';

// Frontend route → group key mapping for permission checks
/**
 * How often an open dashboard tab pings the backend to keep its session alive.
 * 5 min is far below the 12h renewal threshold, so a tab that stays open never
 * ages out; background-tab timer throttling (~1/min in Chrome) is harmless at
 * this cadence.
 */
const AUTH_HEARTBEAT_MS = 5 * 60 * 1000;

// Route guard: schickt Nutzer ohne Zugriff auf ihre Startseite (helpers/routeAccess.ts)
const RouteGuard: FC<{ children: React.ReactNode }> = ({ children }) => {
  const userInfo = useContext(UserContext);
  const location = useLocation();

  if (!userInfo) {
    return <>{children}</>;
  }

  const target = resolveRouteRedirect(location.pathname, userInfo.allowed_groups);
  if (target) {
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};

const App: FC = () => {
  const [theme, colorMode] = useMode();
  // const isDev = process.env.REACT_APP_SERVER_TYPE === 'development';
  const [filterState, setFilterState] = useState<IFilterState>(() => {
    // Read URL params synchronously before first render
    const urlParams = new URLSearchParams(window.location.search);
    const startDateParam = urlParams.get('startDate');
    const endDateParam = urlParams.get('endDate');

    return {
      startDate:
        startDateParam && moment(startDateParam, 'YYYY-MM-DD', true).isValid()
          ? moment(startDateParam, 'YYYY-MM-DD').toDate()
          : moment().subtract(14, 'days').toDate(),
      endDate:
        endDateParam && moment(endDateParam, 'YYYY-MM-DD', true).isValid()
          ? moment(endDateParam, 'YYYY-MM-DD').toDate()
          : new Date(),
      customers: [],
      newsletters: [],
      display: 'daily',
      articleGrouping: 'author',
      articleType: 'all',
      viewType: 'ft',
      viewChannel: 'all',
      viewReferrer: 'all',
      leadValue: 8,
      leadPrice: 12.5,
      samGrouping: 'send_type',
      customerData: [],
      hostName: 'finanztrends.de',
      leadSource: ['all'],
      leadStatus: 'all',
      lpsLeadsGroupBy: 'lead_status',
      productActive: [1],
      materialActive: [],
      network: ['Finanztrends'],
      verlag: ['GeVestor', 'Investor'],
      vnrVerlag: [],
      theme: [],
      vnrAggregation: 'auto',
      custom: {},
    };
  });

  // Create stable date objects to prevent infinite re-renders
  const threeMonthsAgo = useMemo(() => moment().subtract(3, 'months').toDate(), []);
  // Default cohort start for /iterable-statistics: first date after the
  // 15.07.2025 bulk-migration + Aug 2025 stabilization period. Marketing
  // can pick any other date via the FilterBar; edge-case guardrails on the
  // Churn-Analyse block handle problematic ranges.
  const iterableCohortDefault = useMemo(() => moment('2025-09-01').toDate(), []);

  // Authentication state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const isDevelopment = process.env.NODE_ENV === 'development';
  const location = useLocation();
  // MSM-Partner-Dashboard: eigenes Layout ohne internes Menue (auch fuer interne Nutzer, die es oeffnen).
  const partnerView = location.pathname.startsWith('/msm-partner');

  // Validate authentication on mount
  useEffect(() => {
    const validateAuth = async () => {
      // Check if user was previously logged in
      const storedLogin = localStorage.getItem('isLoggedIn');

      // In development mode, bypass authentication
      if (isDevelopment) {
        setIsLoggedIn(true);
        // REACT_APP_DEV_ALLOWED_GROUPS=msm_partner simuliert im Dev-Modus einen eingeschraenkten Nutzer
        // (nur die Oberflaeche; die API bleibt im Dev voll offen).
        const devGroups = process.env.REACT_APP_DEV_ALLOWED_GROUPS;
        setUserInfo({
          username: 'dev',
          email: 'dev@localhost',
          displayName: 'Developer',
          allowed_groups: devGroups ? devGroups.split(',').map((g) => g.trim()).filter(Boolean) : null,
          is_admin: !devGroups,
        });
        setAuthLoading(false);
        return;
      }

      // If not logged in, skip validation
      if (!storedLogin) {
        setIsLoggedIn(false);
        setAuthLoading(false);
        return;
      }

      // Validate token with backend
      try {
        const { api } = await import('./helpers/apiUtils');
        const response = await api.get('/api/auth/validate');

        if (response.data.success) {
          setIsLoggedIn(true);
          // Update stored user info if available
          if (response.data.data.user) {
            const user = response.data.data.user;
            localStorage.setItem('user', JSON.stringify(user));
            setUserInfo({
              username: user.username || user.user_login || '',
              email: user.email || user.user_email || '',
              displayName: user.displayName || user.user_display_name || '',
              allowed_groups: user.allowed_groups ?? null,
              is_admin: user.is_admin ?? false,
            });
          }
        } else {
          // Invalid token
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('user');
          setIsLoggedIn(false);
        }
      } catch (error) {
        // Token validation failed
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('user');
        setIsLoggedIn(false);
      } finally {
        setAuthLoading(false);
      }
    };

    validateAuth();
  }, [isDevelopment]);

  // Session heartbeat.
  //
  // The backend renews the auth cookie once its remaining lifetime drops below
  // 12h, but only on an AUTHENTICATED request — and the Live-Admin main tab has
  // none: its only recurring call is the public /api/live/status poll. An
  // operator sitting on that page for an hour before a broadcast therefore
  // renews nothing and gets thrown out on the first real action (GO LIVE).
  // This tick supplies that request.
  //
  // It renews, it does not police: deliberately a bare axios call rather than
  // the shared `api` instance, so a 401 here CANNOT trigger the interceptor's
  // hard redirect. Otherwise any passing 502 or backend restart would eject an
  // idle user mid-work. A genuinely dead session still surfaces on the next
  // real action, exactly as today.
  useEffect(() => {
    if (!isLoggedIn) return;
    const tick = () => {
      axios.get(`${getApiBaseUrl()}/api/auth/validate`, { withCredentials: true }).catch((err) => {
        console.warn('[Auth] heartbeat failed:', err?.response?.status ?? err?.message);
      });
    };
    const id = setInterval(tick, AUTH_HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [isLoggedIn]);

  // Show loading spinner during auth validation
  if (authLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <FilterContext.Provider value={{ filterState, setFilterState }}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <div className="app">
            <Helmet>
              <script src="http://localhost:8097"></script>
            </Helmet>
            {isLoggedIn ? (
              <UserContext.Provider value={userInfo}>
                {partnerView ? (
                  <RouteGuard>
                    <MsmPartnerPage />
                  </RouteGuard>
                ) : (
                <>
                <SeitenwechselScroll />
                <Sidebar />
                <main className="content">
                  <RouteGuard>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <>
                            <FilterBar
                              filterConfig={['startDate', 'endDate', 'leadSource', 'hostName']}
                            />
                            <Dashboard />
                          </>
                        }
                      />
                      <Route
                        path="/daily-controlling"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate', 'leadStatus']} />
                            <UnifiedDailyControllingPage
                              defaultLeadStatus="new"
                              defaultStartDate={threeMonthsAgo}
                            />
                          </>
                        }
                      />
                      <Route
                        path="/adserver"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate', 'customers']} />
                            <AdServerStatistics />
                          </>
                        }
                      />
                      <Route
                        path="/calendar"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <Calendar />
                          </>
                        }
                      />
                      <Route
                        path="/newsletter"
                        element={
                          <>
                            <FilterBar
                              filterConfig={['startDate', 'endDate', 'samGrouping', 'customers']}
                            />
                            <Newsletter />
                          </>
                        }
                      />
                      <Route
                        path="/sam-timeline"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <SamTimeline />
                          </>
                        }
                      />
                      <Route
                        path="/newsletter-ads-timeline"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate', 'customers']} />
                            <NewsletterAdsTimeline />
                          </>
                        }
                      />
                      <Route
                        path="/newsletter-ad-insertions"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <NewsletterAdInsertions />
                          </>
                        }
                      />
                      <Route
                        path="/ga4-traffic"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} syncWithUrl={true} />
                            <Ga4TrafficPage />
                          </>
                        }
                      />
                      <Route path="/broker-hub" element={<BrokerHub />} />
                      <Route path="/broker-content" element={<BrokerContentHub />} />
                      <Route
                        path="/ad-statistics"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate', 'customers']} />
                            <AdStatistics />
                          </>
                        }
                      />
                      <Route
                        path="/etkp-development"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <EtkpDevelopment />
                          </>
                        }
                      />
                      <Route
                        path="/email-history"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <EmailHistory />
                          </>
                        }
                      />
                      <Route
                        path="/email-deliverability"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <PostmasterDashboard />
                          </>
                        }
                      />
                      <Route
                        path="/anfo-statistics"
                        element={
                          <>
                            <AltSeiteHinweis
                              neu={[
                                { to: '/auswertungen/umsatz/anmeldung-bestellung', label: 'Vom neuen Lead zur Bestellung' },
                              ]}
                            />
                            <AnfoStatistics />
                          </>
                        }
                      />
                      <Route path="/ft-lifetime-value" element={<FtLifetimeValue />} />
                      <Route path="/auswertungen" element={<AuswertungenUebersicht />} />
                      <Route
                        path="/auswertungen/webinare-produkte"
                        element={<AuswertungenWebinareProdukte />}
                      />
                      <Route
                        path="/auswertungen/webinare/:webinarId"
                        element={<AuswertungenWebinarDetail />}
                      />
                      <Route path="/auswertungen/umsatz" element={<AuswertungenUmsatz />} />
                      <Route
                        path="/auswertungen/umsatz/anmeldung-bestellung"
                        element={<AuswertungenAnmeldungBestellung />}
                      />
                      <Route path="/auswertungen/umsatz/kuendigung" element={<KuendigungUnterseite />} />
                      <Route path="/auswertungen/umsatz/cashflow" element={<CashflowUnterseite />} />
                      <Route path="/auswertungen/umsatz/anfo-wert" element={<AuswertungenAnfoWertModell />} />
                      <Route path="/auswertungen/kanaele" element={<AuswertungenKanaele />} />
                      <Route path="/auswertungen/hebel" element={<AuswertungenHebel />} />
                      {/* --- P5 --- */}
                      <Route path="/auswertungen/bestellungen" element={<AuswertungenBestellungen />} />
                      <Route path="/auswertungen/werkzeuge" element={<AuswertungenWerkzeuge />} />
                      <Route path="/auswertungen/umsatz/diskrepanzen" element={<DiskrepanzenUnterseite />} />
                      <Route path="/auswertungen/umsatz/anfo-bewertung" element={<AnfoBewertungUnterseite />} />
                      <Route
                        path="/ad-evaluation"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <AdEvaluation />
                          </>
                        }
                      />
                      {/* P5: Alias mit Query (vorher <Navigate> ohne Query); ?webinar= und die Werkzeug-Reiter in die Sektion. */}
                      <Route path="/webinar-statistics" element={<Umleitung />} />
                      <Route
                        path="/webinar-user-verhalten"
                        element={
                          <>
                            <AltSeiteHinweis
                              neu={[{ to: '/auswertungen/webinare-produkte', label: 'Webinare & Produkte' }]}
                            />
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <WebinarUserVerhalten />
                          </>
                        }
                      />
                      <Route
                        path="/webinar-auswertungen"
                        element={
                          <Umleitung
                            sonst={
                              <>
                                <AltSeiteHinweis
                                  neu={[
                                    { to: '/auswertungen/webinare-produkte', label: 'Webinare & Produkte' },
                                    { to: '/auswertungen/werkzeuge', label: 'Werkzeuge' },
                                  ]}
                                  zusatz="Die Reiter Webinar User Mappings, Affiliate Gruppierungen, Design-A/B und Kennzahlen & Formeln stehen schon unter Werkzeuge."
                                />
                                <FilterBar filterConfig={['startDate', 'endDate']} />
                                <WebinarStatistics />
                              </>
                            }
                          />
                        }
                      />
                      <Route path="/webinar-auswertungen/:webinarId" element={<Umleitung />} />
                      <Route path="/vnr-revenue" element={<VnrRevenue />} />
                      <Route
                        path="/traffic-leads"
                        element={
                          <>
                            <FilterBar
                              filterConfig={[
                                'startDate',
                                'endDate',
                                'lpsLeadsGroupBy',
                                'leadSource',
                                'leadStatus',
                                'display',
                              ]}
                            />
                            <TrafficLeads />
                          </>
                        }
                      />
                      <Route
                        path="/sold-leads"
                        element={
                          <>
                            <FilterBar
                              filterConfig={['startDate', 'endDate', 'network', 'verlag']}
                            />
                            <SoldLeads />
                          </>
                        }
                      />
                      <Route
                        path="/adserver/product-statistics"
                        element={
                          <>
                            <FilterBar
                              filterConfig={[
                                'startDate',
                                'endDate',
                                'customers',
                                'productActive',
                                'materialActive',
                              ]}
                            />
                            <ProductStatisticsPage />
                          </>
                        }
                      />
                      <Route
                        path="/adserver/ad-forms"
                        element={
                          <>
                            <FilterBar
                              filterConfig={[
                                'startDate',
                                'endDate',
                                'customers',
                                'materialActive',
                                'newsletters',
                              ]}
                            />
                            <AdFormStatisticsPage />
                          </>
                        }
                      />
                      <Route
                        path="/adserver/welcome-flows"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate', 'leadPrice']} />
                            <WelcomeFlowsPage />
                          </>
                        }
                      />
                      <Route
                        path="/adserver/sam-scheduling"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <SamSchedulingPage />
                          </>
                        }
                      />
                      <Route
                        path="/adserver/send-pauses"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <AdserverSendPausesPage />
                          </>
                        }
                      />
                      <Route
                        path="/adserver/external-sam-config"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <ExternalSamConfigPage />
                          </>
                        }
                      />
                      <Route
                        path="/mailchimp"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate', 'display']} />
                            <ListGrowth />
                          </>
                        }
                      />
                      <Route
                        path="/mailchimp/client-stats"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <ClientStats />
                          </>
                        }
                      />
                      <Route
                        path="/iterable-statistics"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} syncWithUrl={true} />
                            <IterableStatistics defaultStartDate={iterableCohortDefault} />
                          </>
                        }
                      />
                      <Route
                        path="/vnr/roi"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <RoiEvaluationPage />
                          </>
                        }
                      />
                      <Route
                        path="/vnr/roi-tests"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <RoiTestsPage />
                          </>
                        }
                      />
                      <Route
                        path="/vnr/snapshot-evaluation"
                        element={
                          <>
                            <SnapshotEvaluationPage />
                          </>
                        }
                      />
                      <Route
                        path="/redaktion"
                        element={
                          <>
                            <FilterBar
                              filterConfig={[
                                'startDate',
                                'endDate',
                                'articleGrouping',
                                'articleType',
                                'viewType',
                                'viewChannel',
                                'viewReferrer',
                                'leadValue',
                              ]}
                              syncWithUrl={true}
                            />
                            <ArticleStatistics />
                          </>
                        }
                      />
                      <Route
                        path="/redaktion/artikel-auswertung"
                        element={
                          <>
                            <FilterBar
                              filterConfig={['startDate', 'endDate', 'articleType']}
                              syncWithUrl={true}
                            />
                            <ArticleEvaluationPage />
                          </>
                        }
                      />
                      <Route
                        path="/redaktion/leadwall"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} syncWithUrl={true} />
                            <LeadwallEvaluationPage />
                          </>
                        }
                      />
                      <Route
                        path="/redaktion/leadwall-channel-test"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} syncWithUrl={true} />
                            <LeadwallChannelTestPage />
                          </>
                        }
                      />
                      <Route
                        path="/push"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate', 'customers']} />
                            <PushStatisticsPage />
                          </>
                        }
                      />
                      <Route
                        path="/web-push"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <WebPushDashboard />
                          </>
                        }
                      />
                      <Route
                        path="/dnv-migration"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <DnvMigrationDashboard />
                          </>
                        }
                      />
                      <Route path="/welcome-campaign" element={<WelcomeCampaignPage />} />
                      <Route
                        path="/leads/exports"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate', 'display']} />
                            <ExportedLeadsPage />
                          </>
                        }
                      />
                      <Route
                        path="/boerse-global/article-distribution"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <ArticleDistributionPage />
                          </>
                        }
                      />
                      <Route
                        path="/gurupress/lead-turnover"
                        element={
                          <>
                            <LeadTurnoverPage />
                          </>
                        }
                      />
                      <Route
                        path="/gurupress/lead-imports"
                        element={
                          <>
                            <LeadImportsPage />
                          </>
                        }
                      />
                      <Route
                        path="/test"
                        element={
                          <>
                            <DailyControllingEmail />
                          </>
                        }
                      />
                      <Route
                        path="/doi-evaluation"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <DoiEvaluationPage />
                          </>
                        }
                      />
                      <Route
                        path="/doi-evaluation-optin-emails"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <DoiEvaluationOptinEmailsPage />
                          </>
                        }
                      />
                      <Route
                        path="/doi-process-details"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <DoiProcessDetailsPage />
                          </>
                        }
                      />
                      <Route
                        path="/doi-evaluation-affiliates"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <DoiEvaluationAffiliatesPage />
                          </>
                        }
                      />
                      <Route
                        path="/coreg-efficiency"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} syncWithUrl={true} />
                            <CoregEfficiencyPage />
                          </>
                        }
                      />
                      <Route
                        path="/doi-evaluation-adref"
                        element={
                          <>
                            <FilterBar filterConfig={['startDate', 'endDate']} />
                            <DoiEvaluationAdrefPage />
                          </>
                        }
                      />
                      <Route path="/plx-orders" element={<Umleitung />} />
                      {/* P5: die alte Anfo-Übersicht bis zur Abnahme (Funktionen laut Checkliste nicht alle umgezogen). */}
                      <Route
                        path="/plx-orders/alt"
                        element={
                          <>
                            <AltSeiteHinweis neu={[{ to: '/auswertungen/bestellungen', label: 'Bestellungen' }]} />
                            <PlxOrdersPage />
                          </>
                        }
                      />
                      <Route path="/plx-orders/config" element={<PlxOrdersConfigPage />} />
                      <Route path="/plx-orders/products" element={<PlxProductsPage />} />
                      <Route path="/plx-orders/groupings" element={<PlxGroupingsPage />} />
                      <Route path="/plx-orders/kuendigungsquoten" element={<Umleitung />} />
                      <Route path="/plx-orders/px-import" element={<PlxPxImportPage />} />
                      <Route path="/plx-orders/revenue-variance" element={<Umleitung />} />
                      <Route path="/plx-orders/anfo-valuation" element={<Umleitung />} />
                      <Route path="/plx-orders/cashflow-forecast" element={<Umleitung />} />
                      <Route
                        path="/admin/allowed-emails"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <AllowedEmails />
                          </>
                        }
                      />
                      <Route
                        path="/admin/error-log"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <ErrorLog />
                          </>
                        }
                      />
                      <Route
                        path="/webinar-mailings"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <WebinarMailings />
                          </>
                        }
                      />
                      <Route
                        path="/webinar-management"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <WebinarManagement />
                          </>
                        }
                      />
                      <Route
                        path="/webinar-lp"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <WebinarLp />
                          </>
                        }
                      />
                      <Route
                        path="/webinar-verteiler"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <WebinarVerteiler />
                          </>
                        }
                      />
                      <Route
                        path="/plx-mailings"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <PlxMailings />
                          </>
                        }
                      />
                      <Route
                        path="/newsletter-redlinks"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <NewsletterRedlinks />
                          </>
                        }
                      />
                      <Route
                        path="/nfa-mailings"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <NfaMailings />
                          </>
                        }
                      />
                      <Route
                        path="/dnv-asset-analysis"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <DnvAssetAnalysis />
                          </>
                        }
                      />
                      <Route
                        path="/settings/lead-source-rules"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <LeadSourceRulesPage />
                          </>
                        }
                      />
                      <Route
                        path="/live-admin"
                        element={
                          <>
                            <FilterBar filterConfig={[]} />
                            <LiveAdmin />
                          </>
                        }
                      />
                    </Routes>
                  </RouteGuard>
                </main>
                </>
                )}
              </UserContext.Provider>
            ) : (
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            )}
          </div>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </FilterContext.Provider>
  );
};

export default App;
